import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { To } from 'react-router-dom';

import {
  useGoBack as useGoBackOriginal,
  useNavigateWithTransition as useNavigateWithTransitionOriginal,
} from '@lemoncloud/react-page-transition';

import { isRunningInNativeApp } from '../utils/native-host';

import type {
  NavigateWithTransitionFn,
  PageTransitionConfig,
  PlatformType,
  TransitionNavigateOptions,
} from '@lemoncloud/react-page-transition';

const detectPlatform = (): PlatformType | undefined => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return undefined;
};

const pageTransitionConfig: PageTransitionConfig = { detectPlatform };

const HOME_ROUTE = '/';

// Options passed on every navigation. (The native-shell gate is read in the hook
// bodies below rather than at module scope: it is not ready at import time.)
//
//   legacyFlushSync: commits the route synchronously inside the View Transitions
//   callback. The library sets the scroll position in that same callback, so a
//   synchronous commit guarantees the new page is laid out AND scrolled before
//   the API captures the new snapshot; without it the snapshot races React's
//   async commit and the platform slide flashes on the iOS WebView.
//
//   No `scrollRoot`: the document is the scroller, and without the key the
//   library reads and writes `window` scroll — which is what forward
//   navigation needs to reset the new page to the top.
//
// The library's own saved positions cover the forward half only; restoring on
// back is the consuming app's job. The consumer's `useScrollRestoration` doc
// comment has the why.
const transitionOptions = (): TransitionNavigateOptions => ({
  legacyFlushSync: true,
});

/**
 * Whether there is an in-app history entry to pop back to. React Router stamps
 * an incrementing `idx` onto every entry it creates; entry 0 is the one the
 * document was opened on, so popping from it leaves the app entirely.
 */
const canPopHistory = (): boolean => {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
};

/**
 * Navigate with a native-style page transition (iOS slide / Android lift)
 * when running inside the native shell; plain react-router navigation in a
 * browser. Drop-in replacement for `useNavigate` for intra-app navigation.
 */
export const useNavigateWithTransition = (): NavigateWithTransitionFn => {
  const isNativeApp = isRunningInNativeApp();
  const plainNavigate = useNavigate();
  const transitionNavigate =
    useNavigateWithTransitionOriginal(pageTransitionConfig);

  const nativeNavigate = useCallback(
    (to: To | number, options?: TransitionNavigateOptions): Promise<void> =>
      transitionNavigate(to, { ...transitionOptions(), ...options }),
    [transitionNavigate],
  );

  const webNavigate = useCallback(
    (to: To | number, options?: TransitionNavigateOptions): Promise<void> => {
      if (typeof to === 'number') {
        plainNavigate(to);
      } else {
        plainNavigate(to, options);
      }
      return Promise.resolve();
    },
    [plainNavigate],
  );

  return isNativeApp ? nativeNavigate : webNavigate;
};

/**
 * Back navigation with the matching reverse transition. Use for header back and
 * close buttons so the leave animation mirrors the enter, and so the pop lands
 * on the entry the user actually came from — which is also what lets the
 * consumer's scroll restoration find a saved position for it.
 *
 * Falls back to the home route when there is no entry to pop — a page opened
 * directly by URL would otherwise navigate the user out of the app.
 */
export const useGoBack = (): (() => Promise<void>) => {
  const isNativeApp = isRunningInNativeApp();
  const navigateWithTransition = useNavigateWithTransition();
  const plainNavigate = useNavigate();
  const goBackWithTransition = useGoBackOriginal(pageTransitionConfig);

  return useCallback((): Promise<void> => {
    if (!canPopHistory()) {
      return navigateWithTransition(HOME_ROUTE, { direction: 'back' });
    }
    if (isNativeApp) {
      return goBackWithTransition(transitionOptions());
    }
    plainNavigate(-1);
    return Promise.resolve();
  }, [
    isNativeApp,
    navigateWithTransition,
    plainNavigate,
    goBackWithTransition,
  ]);
};
